import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { NPS_REVIEW_SUBMITTED, SHOW_MESSAGE, npsReviewSubmitFailed } from '~/actions';
import { logCustomEvent } from '~/api/analytics';
import { LottieThumbUp } from '~/images/lottie';
import { submitNpsAnswer } from '../services/SubmitNpsAnswer';
import type { TNpsAnswer } from '../types/NpsReview';
import type { ISubmitNpsReviewParams } from '../types/SubmitNpsReview.parameters';

type TSubmitNpsReviewError = AxiosError<{ message?: string; error?: string; title?: string }>;

/**
 * Submits the NPS screen's answer. On success the signed-in user's `show_nps`
 * is cleared so the flag-off prompt check stops asking, the analytics event
 * records what was stored (`{ skipped: true }` for an auth-service skip,
 * `{ npsScore }` otherwise), and a skip goes home while a score gets the
 * thank-you popup. A failure stays on the screen and surfaces the server's
 * message through the global error popup, as the saga it replaced did.
 */
export const useSubmitNpsReview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return useMutation<TNpsAnswer, TSubmitNpsReviewError, ISubmitNpsReviewParams>({
    mutationFn: (params) => submitNpsAnswer(params),
    onSuccess: (answer) => {
      dispatch({ type: NPS_REVIEW_SUBMITTED });

      void logCustomEvent(
        'nps_review',
        'skipped' in answer ? { skipped: true } : { npsScore: answer.score },
      );

      if ('skipped' in answer || answer.score === -1) {
        navigate('/');
        return;
      }

      dispatch({
        type: SHOW_MESSAGE,
        payload: {
          popupProps: {
            Icon: LottieThumbUp,
            rightButtonText: 'All Done!',
          },
          title: 'Thank you!',
          message:
            'Thanks for letting us know how we are doing! Our goal is to keep enhancing your experience.',
          route: '/',
        },
      });
    },
    onError: (error) => {
      dispatch(npsReviewSubmitFailed(error.response?.data));
    },
  });
};
